// ─────────────────────────────────────────────
//  ChannelList — the master pane (Phase 5 messaging / UI-SPEC "Information
//  Architecture"). A <nav> of the channels + DMs the current user sees today,
//  grouped under fixed headers in the locked order:
//
//    BROADCAST · MY ROOMS · CARE TEAM · GROUPS · DIRECT MESSAGES
//
//  A group with zero rows is hidden entirely (an observer sees only BROADCAST
//  + DIRECT MESSAGES). Each row is a <button> carrying the human label, a
//  member-count meta (or role meta for DMs), a per-row UnreadBadge, aria-current
//  on the selected row, a bold name + navy left-indicator when unread/selected,
//  and a 44px min touch target.
//
//  Visibility here is UX only (T-05-13) — the server read rule is the boundary.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useMessaging } from "../context/MessagingContext";
import { useAuth } from "../context/AuthContext";
import { unreadCount } from "../domain/messaging";
import { CHANNEL_KINDS } from "../domain/channels";
import UnreadBadge from "./UnreadBadge";
import tokens from "../theme/tokens";

const { color, font, space, radius } = tokens;

// Fixed group order + which channel kinds map into each. Per-OR ("me & my
// supervisor") on top, then the PERMANENT roster groups (All Providers, All
// Doctors, AIC — tinted, non-removable), then removable custom GROUP ROOMS.
// broadcast + careTeam are still derived but intentionally not surfaced here.
const GROUPS = [
  { key: "MY ROOMS", kinds: [CHANNEL_KINDS.perOr], permanent: false, removable: false },
  // Broadcast (all users) + Doctors Only / All Providers / AIC — all roster-
  // derived, auto-shown, non-removable. Broadcast first.
  { key: "PERMANENT GROUPS", kinds: [CHANNEL_KINDS.broadcast, CHANNEL_KINDS.allCrnas, CHANNEL_KINDS.allDoctors, CHANNEL_KINDS.aic, CHANNEL_KINDS.aicCoordination], permanent: true, removable: false },
  { key: "GROUP ROOMS", kinds: [CHANNEL_KINDS.custom], permanent: false, removable: true },
  // DIRECT MESSAGES is rendered from dmContacts, not channel kinds.
];

// Fold any DM partner found in message history (but not on today's roster — e.g.
// someone who messaged earlier and has since departed) into the contact list so
// an existing thread is never orphaned. The roster-driven list (D-08) is the
// primary source; this is a defensive union, deduped by uid.
function contactsFromHistory(dmMessages, uid, known) {
  const extra = new Map();
  for (const m of dmMessages || []) {
    if (!m) continue;
    const otherId = m.senderId === uid ? m.recipientId : m.senderId;
    const otherName = m.senderId === uid ? m.recipientName : m.senderName;
    if (!otherId || otherId === uid || known.has(otherId) || extra.has(otherId)) continue;
    extra.set(otherId, { id: otherId, name: otherName || "Direct message", meta: "Direct message" });
  }
  return Array.from(extra.values());
}

export default function ChannelList({ selectedId, onSelect }) {
  const {
    channels, messagesByChannel, dmMessages, dmContacts: rosterContacts,
    rosterDoctorContacts, rosterProviderContacts, createGroup, removeDM, removeGroup,
  } = useMessaging();
  const { user } = useAuth();
  const uid = user?.uid || null;

  // ── New-group builder (ad-hoc room with hand-picked members) ──
  const [building, setBuilding] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [picked, setPicked] = useState([]); // [{ id, name }]
  const [creating, setCreating] = useState(false);
  const addPicked = (id, list) => {
    const person = (list || []).find((c) => c.id === id);
    if (!person || picked.some((p) => p.id === id)) return;
    setPicked((prev) => [...prev, person]);
  };
  const removePicked = (id) => setPicked((prev) => prev.filter((p) => p.id !== id));
  const resetBuilder = () => { setBuilding(false); setGroupName(""); setPicked([]); };
  // One person picked → open a direct message (no group doc created). Multiple
  // people → create a named, removable group room. (Per spec.)
  const submitGroup = async () => {
    if (picked.length === 0) return;
    if (picked.length === 1) {
      const p = picked[0];
      resetBuilder();
      onSelect({ type: "dm", id: `dm-${p.id}`, contactId: p.id, name: p.name });
      return;
    }
    setCreating(true);
    try {
      const id = await createGroup({ name: groupName.trim(), memberIds: picked.map((p) => p.id) });
      resetBuilder();
      if (id) onSelect({ type: "channel", id });
    } finally {
      setCreating(false);
    }
  };
  const submitLabel = picked.length === 1 ? "Start direct message" : "Create group";

  // Remove (delete) handlers — confirm first; both deletions are irreversible
  // (and would otherwise auto-clear at midnight). Clear the selection if the
  // row being removed is the open one.
  const confirmRemoveDM = (contactId, name) => {
    if (typeof window !== "undefined" && typeof window.confirm === "function"
      && !window.confirm(`Remove your conversation with ${name || "this person"}? This deletes the messages for both of you.`)) return;
    if (selectedId === `dm-${contactId}`) onSelect(null);
    removeDM(contactId);
  };
  const confirmRemoveGroup = (channelId, label) => {
    if (typeof window !== "undefined" && typeof window.confirm === "function"
      && !window.confirm(`Remove the group "${label || "group"}"? This deletes the group and its messages for everyone.`)) return;
    if (selectedId === channelId) onSelect(null);
    removeGroup(channelId);
  };

  // DIRECT MESSAGES lists ACTUAL conversations only (people you've exchanged
  // messages with) — NOT the whole roster. That's what makes the X work: delete
  // erases the messages (deleteDmThread), the partner drops out of history, and
  // the row disappears. To START a new DM, use "New message" (1 person) — the
  // roster picker. Names/roles are enriched from the roster contact when known.
  const rosterById = new Map((rosterContacts || []).map((c) => [c.id, c]));
  const dmContacts = contactsFromHistory(dmMessages, uid, new Set())
    .map((c) => {
      const r = rosterById.get(c.id);
      return r ? { ...c, name: r.name || c.name, meta: r.meta || c.meta } : c;
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // Per-channel unread (channel messages not mine + not in my readBy).
  const channelUnread = (ch) => unreadCount(messagesByChannel?.[ch.id] || [], [], uid);

  // Per-contact unread DMs (to me, not yet read).
  const dmUnread = (contactId) =>
    (dmMessages || []).filter(
      (m) => m && m.senderId === contactId && m.recipientId === uid && !m.isRead
    ).length;

  const hasAnyRows = channels.length > 0 || dmContacts.length > 0;

  // New-group builder UI (shown at the top of the list).
  const groupBuilderUI = (
    <div style={styles.builderWrap}>
      {!building ? (
        <button type="button" style={styles.newGroupBtn} onClick={() => setBuilding(true)}>
          ＋ New message / group
        </button>
      ) : (
        <div style={styles.builder}>
          <div style={styles.builderHint}>
            Add one person for a direct message, or name a group and add several.
          </div>
          <input
            style={styles.builderInput}
            placeholder="Group name (only for groups)"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            aria-label="Group name"
          />
          <select style={styles.builderInput} value="" aria-label="Add a doctor"
            onChange={(e) => addPicked(e.target.value, rosterDoctorContacts)}>
            <option value="">+ Add doctor…</option>
            {rosterDoctorContacts.filter((c) => !picked.some((p) => p.id === c.id)).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select style={styles.builderInput} value="" aria-label="Add a provider"
            onChange={(e) => addPicked(e.target.value, rosterProviderContacts)}>
            <option value="">+ Add provider…</option>
            {rosterProviderContacts.filter((c) => !picked.some((p) => p.id === c.id)).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {picked.length > 0 && (
            <div style={styles.chips}>
              {picked.map((p) => (
                <span key={p.id} style={styles.chip}>
                  {p.name}
                  <button type="button" style={styles.chipX} onClick={() => removePicked(p.id)} aria-label={`Remove ${p.name}`}>✕</button>
                </span>
              ))}
            </div>
          )}
          <div style={styles.builderActions}>
            <button type="button" style={styles.builderCancel} onClick={resetBuilder}>Cancel</button>
            <button type="button" style={styles.builderCreate} disabled={picked.length === 0 || creating} onClick={submitGroup}>
              {creating ? "Creating…" : submitLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Empty IA states (UI-SPEC copy table) ──
  if (!hasAnyRows) {
    return (
      <nav style={styles.nav} aria-label="Conversations">
        {groupBuilderUI}
        <div style={styles.empty}>
          <div style={styles.emptyHead}>You're not in any channels today.</div>
          <div style={styles.emptyBody}>You can still use direct messages or start a group room.</div>
        </div>
      </nav>
    );
  }

  // A row is a flex wrapper holding the main select <button> and, when removable,
  // a sibling X <button> (never nested — invalid HTML). Permanent rows get a
  // tinted background + a 🔒 affordance and no X.
  const renderRow = ({ key, label, meta, selected, unread, onClick, permanent, onRemove, removeLabel }) => (
    <div key={key} style={{ ...styles.rowWrap, ...(permanent ? styles.rowWrapPermanent : {}), ...(selected ? styles.rowSelected : {}) }}>
      <button
        type="button"
        style={styles.row}
        aria-current={selected ? "true" : undefined}
        onClick={onClick}
      >
        <span style={styles.indicator(selected)} aria-hidden="true" />
        <span style={styles.rowText}>
          <span style={{ ...styles.rowName, fontWeight: unread > 0 ? font.weight.bold : font.weight.regular }}>
            {permanent && <span aria-hidden="true" style={styles.lock}>🔒 </span>}
            {label}
          </span>
          <span style={styles.rowMeta}>{meta}</span>
        </span>
        <UnreadBadge count={unread} />
      </button>
      {onRemove && (
        <button
          type="button"
          style={styles.removeBtn}
          onClick={onRemove}
          aria-label={removeLabel || `Remove ${label}`}
          title={removeLabel || `Remove ${label}`}
        >
          ✕
        </button>
      )}
    </div>
  );

  return (
    <nav style={styles.nav} aria-label="Conversations">
      {groupBuilderUI}
      {GROUPS.map((group) => {
        const rows = channels.filter((c) => group.kinds.includes(c.kind));
        if (rows.length === 0) return null; // hide zero-row groups
        return (
          <div key={group.key} role="group" aria-label={group.key}>
            <div style={styles.groupHead}>{group.key}</div>
            {rows.map((ch) =>
              renderRow({
                key: ch.id,
                label: ch.label,
                meta: (() => { const n = ch.count != null ? ch.count : (ch.memberIds || []).length; return `${n} member${n === 1 ? "" : "s"}`; })(),
                selected: selectedId === ch.id,
                unread: channelUnread(ch),
                onClick: () => onSelect({ type: "channel", id: ch.id }),
                permanent: group.permanent,
                onRemove: group.removable ? () => confirmRemoveGroup(ch.id, ch.label) : undefined,
                removeLabel: group.removable ? `Remove group ${ch.label}` : undefined,
              })
            )}
          </div>
        );
      })}

      {dmContacts.length > 0 && (
        <div role="group" aria-label="DIRECT MESSAGES">
          <div style={styles.groupHead}>DIRECT MESSAGES</div>
          {dmContacts.map((c) =>
            renderRow({
              key: `dm-${c.id}`,
              label: c.name,
              meta: c.meta || "Direct message",
              selected: selectedId === `dm-${c.id}`,
              unread: dmUnread(c.id),
              onClick: () => onSelect({ type: "dm", id: `dm-${c.id}`, contactId: c.id, name: c.name }),
              onRemove: () => confirmRemoveDM(c.id, c.name),
              removeLabel: `Remove conversation with ${c.name}`,
            })
          )}
        </div>
      )}
    </nav>
  );
}

const styles = {
  nav: {
    overflowY: "auto",
    height: "100%",
    background: color.surface.card,
    fontFamily: font.family,
  },
  groupHead: {
    fontSize: font.size.label, // 12
    fontWeight: font.weight.bold, // 700
    color: color.text.muted,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    padding: `${space.md}px ${space.md}px ${space.sm}px`,
  },
  // Wrapper holds the select button + optional remove X; carries the row border
  // and the selected/permanent background tints.
  rowWrap: {
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${color.border.default}`,
  },
  rowWrapPermanent: {
    // Distinct tint marking a non-removable, roster-derived permanent group.
    background: "#eef2ff", // indigo-50 — reads as "system / locked"
    borderLeft: `3px solid ${color.accent.primary}`,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    flex: 1,
    minWidth: 0,
    minHeight: 44, // touch target
    background: "transparent",
    border: "none",
    padding: "8px 8px 8px 4px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left",
  },
  removeBtn: {
    flexShrink: 0,
    minWidth: 36,
    minHeight: 44,
    background: "transparent",
    border: "none",
    color: color.text.muted,
    cursor: "pointer",
    fontSize: 13,
    lineHeight: 1,
    padding: "0 12px",
  },
  lock: { fontSize: 11 },
  rowSelected: {
    background: color.surface.raised,
  },
  indicator: (selected) => ({
    width: 3,
    alignSelf: "stretch",
    borderRadius: radius.full,
    background: selected ? color.accent.primary : "transparent",
    flexShrink: 0,
  }),
  rowText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    paddingLeft: space.sm,
  },
  rowName: {
    fontSize: font.size.body, // 14
    color: color.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowMeta: {
    fontSize: font.size.label, // 12
    color: color.text.muted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  builderWrap: {
    padding: `${space.sm}px ${space.sm}px`,
    borderBottom: `1px solid ${color.border.default}`,
  },
  newGroupBtn: {
    width: "100%",
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "8px 10px",
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    cursor: "pointer",
    fontFamily: font.family,
  },
  builder: {
    display: "flex",
    flexDirection: "column",
    gap: space.sm,
  },
  builderHint: {
    fontSize: font.size.label,
    color: color.text.muted,
    lineHeight: font.lineHeight.normal,
  },
  builderInput: {
    width: "100%",
    boxSizing: "border-box",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "8px 10px",
    fontSize: font.size.body,
    fontFamily: font.family,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.full,
    padding: "2px 4px 2px 10px",
    fontSize: font.size.label,
    color: color.text.primary,
  },
  chipX: {
    background: "transparent",
    border: "none",
    color: color.text.muted,
    cursor: "pointer",
    fontSize: 12,
    lineHeight: 1,
    padding: "2px 4px",
  },
  builderActions: { display: "flex", justifyContent: "flex-end", gap: space.sm },
  builderCancel: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: "6px 12px",
    fontSize: font.size.label,
    cursor: "pointer",
  },
  builderCreate: {
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "6px 12px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  empty: {
    padding: space.xl,
    textAlign: "center",
  },
  emptyHead: {
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    marginBottom: space.xs,
  },
  emptyBody: {
    fontSize: font.size.label,
    color: color.text.muted,
    lineHeight: font.lineHeight.normal,
  },
};
